import styled from '@emotion/styled'
import { DatePicker, Input, Select, Switch, Checkbox } from 'antd'
import { QuestionCircleOutlined } from '@ant-design/icons'
import { useFormikContext } from 'formik'
import { useState } from 'react'
import dayjs, { Dayjs } from 'dayjs'
import type {
  CloneCompetitionType,
  CloneTeamsMode,
  WizardFormValues,
} from '@sportninja/common/features/competition/wizard'
import {
  CLONE_COMPETITION_TYPE_OPTIONS,
  CLONE_TEAMS_OPTIONS,
  defaultCloneTeamsForType,
  initialWizardValues,
  isPlayoffsType,
  toCloneCompetitionType,
  toCompetitionDay,
} from '@sportninja/common/features/competition/wizard'
import type { OrgCompetitionListItem } from '@sportninja/common/services/competition'
import SourceCompetitionPicker from '../components/SourceCompetitionPicker'
import { competitionWord } from '@sportninja/common/features/competition/wizard'
import Text from 'src/components/ts-components/Text'
import { paragraphSmall } from 'src/components/css'
import colors from '@sportninja/common/constants/appColors'

type Props = { orgId: string }

const TYPE_HELP_URL =
  'https://support.sportninja.com/playoffs-/-tournaments-set-up-options'

const Step1CloneForm = ({ orgId }: Props) => {
  const { values, setFieldValue, setValues, errors, touched, setFieldTouched } =
    useFormikContext<WizardFormValues>()
  const cs = values.cloneSource
  const [endDateOpen, setEndDateOpen] = useState(false)

  // SN-8028: this component re-renders while a date picker's panel is open and
  // mid-navigation (Formik context churn on focus/hover). A controlled antd
  // DatePicker re-syncs its internal panel view to `value`'s month on every
  // render, which snapped an open panel back from a freshly-navigated year
  // (e.g. 2027) to the value's month — so a day click resolved against the
  // stale month and emitted the wrong date (or appeared to "revert").
  //
  // Fix: own the open panel month in local state (`pickerValue`) so re-renders
  // can't reset it. We seed it from the current value when the panel opens and
  // then follow every panel navigation. This keeps `value` controlled for
  // display while making the *panel position* immune to parent re-renders.
  const [startPanel, setStartPanel] = useState<Dayjs | null>(null)
  const [endPanel, setEndPanel] = useState<Dayjs | null>(null)

  const fieldError = (key: keyof typeof cs): string | undefined => {
    if (!touched.cloneSource?.[key]) {
      return undefined
    }
    if (!errors.cloneSource || typeof errors.cloneSource === 'string') {
      return undefined
    }
    const msg = errors.cloneSource[key]
    return typeof msg === 'string' ? msg : undefined
  }

  const handleSourceChange = (item: OrgCompetitionListItem | null) => {
    // Reset clone-form values whenever the source changes; pre-fill from the
    // newly-selected competition (mirrors the action-box clone entry point).
    if (!item) {
      setValues({
        ...values,
        cloneSource: initialWizardValues.cloneSource,
      })
      return
    }
    const resolvedType = toCloneCompetitionType(item.competition_type)
    // Playoffs (Simple or Enhanced) never act as a registration competition —
    // ignore the source's is_registration_on flag so the locked toggle and
    // payload stay consistent.
    const isRegistrationSchedule = isPlayoffsType(resolvedType)
      ? false
      : Boolean(item.is_registration_on)
    setValues({
      ...values,
      cloneSource: {
        ...initialWizardValues.cloneSource,
        sourceScheduleId: item.id,
        sourceName: item.name_full ?? null,
        name: item.name_full ? `Copy of ${item.name_full}` : '',
        startsAt: toCompetitionDay(item.starts_at),
        endsAt: toCompetitionDay(item.ends_at),
        competitionType: resolvedType,
        isTournament:
          resolvedType === 'playoffs_enhanced' || resolvedType === 'tournament',
        isRegistrationSchedule,
        // Simple Playoffs defaults to bringing teams over with full rosters.
        cloneTeams: defaultCloneTeamsForType(resolvedType),
      },
    })
  }

  const typeOptions = CLONE_COMPETITION_TYPE_OPTIONS.map((opt) => ({
    value: opt.value,
    label: (
      <OptionContent>
        <Text variant='paragraphSmall' color={colors.WHITE}>
          {opt.label}
        </Text>
        <Text variant='paragraphXSmall' color={colors.NEUTRAL_50}>
          {opt.description}
        </Text>
      </OptionContent>
    ),
  }))

  // Playoffs (Simple or Enhanced) never act as a registration competition —
  // lock the Registration toggle off and dim the label to match the existing
  // disabled-checkbox treatment.
  const isPlayoffsClone = isPlayoffsType(cs.competitionType)
  const canToggleRegistration = !isPlayoffsClone
  // Clone Registration Settings is only available when the cloned competition
  // is itself a registration competition.
  const canCloneRegistrationSettings = cs.isRegistrationSchedule

  const handleRegistrationToggle = (v: boolean) => {
    setFieldValue('cloneSource.isRegistrationSchedule', v)
    if (v) {
      // Registration competitions can't bring teams over — reset to default.
      setFieldValue('cloneSource.cloneTeams', 'none')
    } else {
      setFieldValue('cloneSource.cloneRegistrationSettings', false)
    }
  }

  return (
    <Container>
      <Header>
        <Text variant='heading3' weight='semiBold' color={colors.WHITE}>
          Cloning {competitionWord()}
        </Text>
        <Text variant='paragraphMedium' color={colors.NEUTRAL_50}>
          Review and customize the details for your new{' '}
          {competitionWord({ capitalize: false })}.
        </Text>
      </Header>

      <Form>
        <Field>
          <Label>
            <Text variant='paragraphSmall' weight='medium' color={colors.WHITE}>
              Source {competitionWord()}
            </Text>
          </Label>
          <SourceCompetitionPicker
            orgId={orgId}
            value={cs.sourceScheduleId}
            onChange={handleSourceChange}
          />
          {fieldError('sourceScheduleId') ? (
            <Text variant='paragraphXSmall' color={colors.ERROR_200}>
              {fieldError('sourceScheduleId')}
            </Text>
          ) : null}
        </Field>

        <Field>
          <Label>
            <Text variant='paragraphSmall' weight='medium' color={colors.WHITE}>
              {competitionWord()} Name
            </Text>
          </Label>
          <Input
            value={cs.name}
            onChange={(e) => setFieldValue('cloneSource.name', e.target.value)}
            onBlur={() => setFieldTouched('cloneSource.name', true)}
            placeholder={`Copy of New ${competitionWord()}`}
          />
          {fieldError('name') ? (
            <Text variant='paragraphXSmall' color={colors.ERROR_200}>
              {fieldError('name')}
            </Text>
          ) : null}
        </Field>

        <TwoColumnGrid>
          <Field>
            <Label>
              <Text
                variant='paragraphSmall'
                weight='medium'
                color={colors.WHITE}
              >
                Start Date
              </Text>
            </Label>
            <DatePicker
              style={{ width: '100%' }}
              value={cs.startsAt ? dayjs(cs.startsAt) : null}
              pickerValue={startPanel ?? undefined}
              onOpenChange={(open) => {
                // Seed the panel from the current value each time it opens; from
                // then on the local state owns the panel month so re-renders
                // can't reset it mid-navigation (SN-8028).
                if (open) {
                  setStartPanel(cs.startsAt ? dayjs(cs.startsAt) : dayjs())
                }
              }}
              onPickerValueChange={(v, info) => {
                // Only follow real panel navigation. antd also fires this with
                // `source: 'reset'` on re-render (re-deriving the panel from
                // `value`), which would clobber an in-progress year/month jump
                // and snap the calendar back — the SN-8028 revert.
                if (info?.source === 'panel') {
                  setStartPanel(v)
                }
              }}
              onChange={(d: Dayjs | null) => {
                setFieldTouched('cloneSource.startsAt', true)
                setFieldValue(
                  'cloneSource.startsAt',
                  d ? d.format('YYYY-MM-DD') : null
                )
                setFieldValue('cloneSource.endsAt', null)
                if (d) {
                  setEndDateOpen(true)
                }
              }}
              onBlur={() => setFieldTouched('cloneSource.startsAt', true)}
              format='DD/MM/YYYY'
              placeholder='DD/MM/AAAA'
            />
            {fieldError('startsAt') ? (
              <Text variant='paragraphXSmall' color={colors.ERROR_200}>
                {fieldError('startsAt')}
              </Text>
            ) : null}
          </Field>
          <Field>
            <Label>
              <Text
                variant='paragraphSmall'
                weight='medium'
                color={colors.WHITE}
              >
                End Date
              </Text>
            </Label>
            <DatePicker
              style={{ width: '100%' }}
              open={endDateOpen}
              value={cs.endsAt ? dayjs(cs.endsAt) : null}
              pickerValue={endPanel ?? undefined}
              onOpenChange={(open) => {
                setEndDateOpen(open)
                // Seed the panel on open: land on the existing end date, else
                // the start month, else today. Local state then owns the panel
                // month so re-renders can't reset it mid-navigation (SN-8028).
                if (open) {
                  setEndPanel(
                    cs.endsAt
                      ? dayjs(cs.endsAt)
                      : cs.startsAt
                      ? dayjs(cs.startsAt)
                      : dayjs()
                  )
                }
              }}
              onPickerValueChange={(v, info) => {
                // See START picker: ignore the `reset` re-derivation so an
                // in-progress panel jump survives re-renders (SN-8028).
                if (info?.source === 'panel') {
                  setEndPanel(v)
                }
              }}
              onChange={(d: Dayjs | null) => {
                setFieldTouched('cloneSource.endsAt', true)
                setFieldValue(
                  'cloneSource.endsAt',
                  d ? d.format('YYYY-MM-DD') : null
                )
              }}
              onBlur={() => setFieldTouched('cloneSource.endsAt', true)}
              format='DD/MM/YYYY'
              placeholder='DD/MM/AAAA'
              disabledDate={(current) =>
                !!cs.startsAt && current.isBefore(dayjs(cs.startsAt), 'day')
              }
            />
            {fieldError('endsAt') ? (
              <Text variant='paragraphXSmall' color={colors.ERROR_200}>
                {fieldError('endsAt')}
              </Text>
            ) : null}
          </Field>
        </TwoColumnGrid>

        <Field>
          <LabelRow>
            <Text variant='paragraphSmall' weight='medium' color={colors.WHITE}>
              {competitionWord()} Type
            </Text>
            <LearnMore
              type='button'
              onClick={() => window.open(TYPE_HELP_URL, '_blank')}
            >
              <Text variant='paragraphXSmall' color={colors.PRIMARY}>
                Learn more
              </Text>
              <QuestionCircleOutlined
                style={{ fontSize: 16, color: colors.PRIMARY }}
              />
            </LearnMore>
          </LabelRow>
          <Select
            style={{ width: '100%' }}
            options={typeOptions}
            value={cs.competitionType ?? undefined}
            onChange={(v) => {
              const next = v as CloneCompetitionType
              setFieldTouched('cloneSource.competitionType', true)
              setFieldValue('cloneSource.competitionType', next)
              // is_tournament tracks Enhanced Playoffs / Tournament selection.
              setFieldValue(
                'cloneSource.isTournament',
                next === 'playoffs_enhanced' || next === 'tournament'
              )
              // Apply the type's default Clone Teams option (Simple Playoffs →
              // full rosters; everything else → initial value).
              setFieldValue(
                'cloneSource.cloneTeams',
                defaultCloneTeamsForType(next)
              )
              if (isPlayoffsType(next)) {
                // Playoffs can't be a registration competition — force the
                // toggle off so the locked state matches the data.
                setFieldValue('cloneSource.isRegistrationSchedule', false)
                setFieldValue('cloneSource.cloneRegistrationSettings', false)
              }
            }}
            onBlur={() => setFieldTouched('cloneSource.competitionType', true)}
            placeholder={`Select a ${competitionWord({
              capitalize: false,
            })} type`}
          />
          {fieldError('competitionType') ? (
            <Text variant='paragraphXSmall' color={colors.ERROR_200}>
              {fieldError('competitionType')}
            </Text>
          ) : null}
        </Field>

        <Divider />

        <SwitchRow>
          <SwitchText>
            <Text
              variant='paragraphSmall'
              weight='medium'
              color={canToggleRegistration ? colors.WHITE : colors.NEUTRAL_100}
            >
              Registration {competitionWord()}
            </Text>
            <Text
              variant='paragraphXSmall'
              color={
                canToggleRegistration ? colors.NEUTRAL_50 : colors.NEUTRAL_100
              }
            >
              Turn this ON if the cloned{' '}
              {competitionWord({ capitalize: false })} will be used for
              registration purposes.
            </Text>
          </SwitchText>
          <Switch
            size='small'
            disabled={!canToggleRegistration}
            checked={cs.isRegistrationSchedule}
            onChange={handleRegistrationToggle}
          />
        </SwitchRow>

        <CheckboxList>
          <Checkbox
            disabled={!canCloneRegistrationSettings}
            checked={cs.cloneRegistrationSettings}
            onChange={(e) =>
              setFieldValue(
                'cloneSource.cloneRegistrationSettings',
                e.target.checked
              )
            }
          >
            <Text
              variant='paragraphSmall'
              weight='medium'
              color={
                canCloneRegistrationSettings ? colors.WHITE : colors.NEUTRAL_100
              }
            >
              Clone Registration Settings
            </Text>
          </Checkbox>
        </CheckboxList>

        {cs.isRegistrationSchedule ||
        cs.competitionType === 'playoffs_enhanced' ? null : (
          <>
            <Divider />
            <Field>
              <LabelRow>
                <Text
                  variant='paragraphSmall'
                  weight='medium'
                  color={colors.WHITE}
                >
                  Clone Teams
                </Text>
              </LabelRow>
              <Select
                style={{ width: '100%' }}
                options={CLONE_TEAMS_OPTIONS.map((o) => ({
                  value: o.value,
                  label: o.label,
                }))}
                value={cs.cloneTeams}
                onChange={(v) =>
                  setFieldValue('cloneSource.cloneTeams', v as CloneTeamsMode)
                }
              />
            </Field>
          </>
        )}

        <Divider />
      </Form>
    </Container>
  )
}

export default Step1CloneForm

// ---- styled ----
const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  width: 100%;

  /* Figma input styling — Ant Input, Select, DatePicker */
  .ant-input,
  .ant-picker {
    ${paragraphSmall};
    background-color: ${colors.SECONDARY_300} !important;
    border: 1px solid ${colors.SECONDARY_200} !important;
    border-radius: 16px !important;
    padding: 16px !important;
    height: auto !important;
    color: ${colors.WHITE} !important;
  }
  .ant-select-single {
    height: auto !important;
    min-height: 54px !important;
  }
  .ant-select .ant-select-selector {
    ${paragraphSmall};
    background-color: ${colors.SECONDARY_300} !important;
    border: 1px solid ${colors.SECONDARY_200} !important;
    border-radius: 16px !important;
    min-height: 54px !important;
    height: auto !important;
    padding: 8px 16px !important;
    color: ${colors.WHITE} !important;
  }
  .ant-select-single .ant-select-selector .ant-select-selection-item,
  .ant-select-single .ant-select-selector .ant-select-selection-placeholder {
    display: flex !important;
    align-items: center !important;
    line-height: normal !important;
    padding-inline-start: 0 !important;
  }
  /* hide blinking text cursor of the search input once a value is selected */
  .ant-select-single .ant-select-selector .ant-select-selection-search,
  .ant-select-single .ant-select-selector .ant-select-selection-search-input {
    inset-inline-start: 16px !important;
    caret-color: transparent !important;
  }
  .ant-select-single .ant-select-selector .ant-select-selection-placeholder {
    color: ${colors.NEUTRAL_100} !important;
  }
  .ant-select .ant-select-arrow {
    inset-inline-end: 16px !important;
    top: 50% !important;
    bottom: auto !important;
    margin: 0 !important;
    height: 12px !important;
    transform: translateY(-50%) !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
  }
  .ant-picker-input > input {
    ${paragraphSmall};
    color: ${colors.WHITE} !important;
  }
  .ant-picker-input > input::placeholder {
    color: ${colors.NEUTRAL_100} !important;
    opacity: 1 !important;
  }
  .ant-input::placeholder {
    color: ${colors.NEUTRAL_100} !important;
    opacity: 1 !important;
  }
  .ant-select-arrow,
  .ant-picker-suffix {
    color: ${colors.WHITE} !important;
  }
  .ant-checkbox-wrapper {
    align-items: flex-start !important;
  }
  /* Unchecked & enabled: white border on transparent fill */
  .ant-checkbox:not(.ant-checkbox-disabled) .ant-checkbox-inner {
    background-color: transparent !important;
    border-color: ${colors.WHITE} !important;
  }
  /* Unchecked & disabled: muted neutral border on transparent fill */
  .ant-checkbox.ant-checkbox-disabled .ant-checkbox-inner {
    background-color: transparent !important;
    border-color: ${colors.SECONDARY_100} !important;
  }
  /* Checked & enabled: primary fill + black checkmark */
  .ant-checkbox-checked:not(.ant-checkbox-disabled) .ant-checkbox-inner {
    background-color: ${colors.PRIMARY} !important;
    border-color: ${colors.PRIMARY} !important;
  }
  .ant-checkbox-checked:not(.ant-checkbox-disabled) .ant-checkbox-inner::after {
    border-color: ${colors.BLACK} !important;
  }
`

const Header = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  text-align: center;
`

const Form = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  max-width: 720px;
`

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const Label = styled.label`
  display: block;
`

const LabelRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const LearnMore = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: transparent;
  border: 0;
  cursor: pointer;
  padding: 0;
  &,
  & * {
    cursor: pointer;
  }
`

const TwoColumnGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
`

const OptionContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 4px 0;
`

const Divider = styled.hr`
  border: 0;
  border-top: 1px solid ${colors.SECONDARY_300};
  margin: 0;
  width: 100%;
`

const SwitchRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
`

const SwitchText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
`

const CheckboxList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`
